import { Component, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import {
  MailWorkflowService,
  FileClassDto,
  FileReferenceDto,
  CreateFileReferenceRequest,
  MailInitiateRequest
} from './mail-workflow.service';

type MailType = 'INCOMING' | 'OUTGOING' | 'INTERNAL_MEMO';
type Priority  = 'NORMAL' | 'URGENT' | 'CONFIDENTIAL';
type StepId    = 1 | 2 | 3 | 4 | 5 | 6;
type RecipientMode = 'USER' | 'GROUP';

interface AlfrescoUser  { id: string; displayName: string; email: string; }
interface AlfrescoGroup { id: string; displayName: string; }

@Component({
  selector: 'app-mail-initiate',
  template: `
    <div class="mi-container">

      <!-- Top bar -->
      <div class="mi-topbar">
        <button class="mi-back-btn" (click)="goBack()">
          <span class="material-icons">arrow_back</span>
        </button>
        <div class="mi-topbar-info">
          <span class="mi-topbar-title">New Mail / Memo</span>
          <span class="mi-topbar-sub">Step {{ currentStep }} of 6</span>
        </div>
        <div class="mi-topbar-actions">
          <button class="mi-btn mi-btn--ghost" (click)="saveDraft()" [disabled]="saving || currentStep < 2">
            <span class="material-icons">save</span>
            Save Draft
          </button>
        </div>
      </div>

      <!-- Body -->
      <div class="mi-body">

        <!-- Steps sidebar -->
        <aside class="mi-steps">
          <div *ngFor="let step of steps; let i = index">
            <div class="mi-step" [class.mi-step--done]="currentStep > step.id"
                 [class.mi-step--active]="currentStep === step.id"
                 (click)="currentStep > step.id ? goToStep(step.id) : null">
              <div class="mi-step-circle">
                <span *ngIf="currentStep > step.id" class="material-icons" style="font-size:13px">check</span>
                <span *ngIf="currentStep <= step.id">{{ step.id }}</span>
              </div>
              <div class="mi-step-info">
                <div class="mi-step-title">{{ step.title }}</div>
                <div class="mi-step-sub">{{ step.subtitle }}</div>
              </div>
            </div>
            <div class="mi-step-connector" *ngIf="i < steps.length - 1"></div>
          </div>
        </aside>

        <!-- Form content -->
        <main class="mi-content">

          <!-- Scrollable step area -->
          <div class="mi-step-content">

          <!-- ===== STEP 1: MAIL TYPE ===== -->
          <ng-container *ngIf="currentStep === 1">
            <div class="mi-section-title">
              <span class="material-icons">mail</span>
              Select Mail Type
            </div>
            <div class="mi-type-grid">
              <div class="mi-type-card" *ngFor="let t of mailTypeOptions"
                   [class.mi-type-card--selected]="mailType === t.value"
                   (click)="selectMailType(t.value)">
                <span class="material-icons mi-type-icon">{{ t.icon }}</span>
                <div class="mi-type-name">{{ t.label }}</div>
                <div class="mi-type-sub">{{ t.description }}</div>
                <div class="mi-type-check" *ngIf="mailType === t.value">
                  <span class="material-icons">check_circle</span>
                </div>
              </div>
            </div>
            <div class="mi-info-box" *ngIf="mailType === 'INTERNAL_MEMO'">
              <span class="material-icons">info</span>
              Internal memos use an OnlyOffice template. You will be taken to the template picker on the next step.
            </div>
            <div class="mi-info-box" *ngIf="mailType === 'OUTGOING'">
              <span class="material-icons">info</span>
              For outgoing replies, you can link this mail to an existing incoming mail after classification.
            </div>
          </ng-container>

          <!-- ===== STEP 2: UPLOAD DOCUMENT ===== -->
          <ng-container *ngIf="currentStep === 2 && mailType !== 'INTERNAL_MEMO'">
            <div class="mi-section-title">
              <span class="material-icons">upload_file</span>
              Attach Document
            </div>
            <div class="mi-upload-zone"
                 *ngIf="!uploadedFile"
                 [class.mi-upload-zone--drag]="isDragging"
                 (dragover)="onDragOver($event)"
                 (dragleave)="isDragging = false"
                 (drop)="onDrop($event)"
                 (click)="fileInput.click()">
              <span class="material-icons mi-upload-icon">cloud_upload</span>
              <div class="mi-upload-title">Drag & drop your document here</div>
              <div class="mi-upload-sub">or click to browse files</div>
              <div class="mi-upload-formats">
                <span class="mi-fmt">PDF</span>
                <span class="mi-fmt">DOCX</span>
                <span class="mi-fmt">DOC</span>
                <span class="mi-fmt">TIFF</span>
                <span class="mi-fmt">JPG</span>
              </div>
              <input #fileInput type="file" style="display:none"
                     accept=".pdf,.doc,.docx,.tiff,.jpg,.jpeg,.png"
                     (change)="onFileSelected($event)">
            </div>
            <div class="mi-upload-progress" *ngIf="uploading">
              <div class="mi-upload-spinner"></div>
              <span>Uploading to Alfresco… {{ uploadedFileName }}</span>
            </div>
            <div class="mi-file-card" *ngIf="uploadedFile && !uploading">
              <span class="material-icons mi-file-icon" [class.mi-file-icon--pdf]="isPdf">picture_as_pdf</span>
              <div class="mi-file-info">
                <div class="mi-file-name">{{ uploadedFile.fileName }}</div>
                <div class="mi-file-meta">{{ formatSize(uploadedFile.fileSize) }} · Uploaded to Alfresco</div>
              </div>
              <div class="mi-file-node">
                <span class="material-icons" style="color:#27ae60;font-size:18px">check_circle</span>
              </div>
              <button class="mi-file-remove" (click)="removeFile()" title="Remove file">
                <span class="material-icons">close</span>
              </button>
            </div>
            <div class="mi-error" *ngIf="uploadError">
              <span class="material-icons">error_outline</span> {{ uploadError }}
            </div>

            <!-- Document preview after upload -->
            <div class="mi-preview-wrap" *ngIf="uploadedFile && !uploading">
              <div class="mi-preview-label">
                <span class="material-icons" style="font-size:16px;vertical-align:middle">preview</span>
                Document Preview
              </div>
              <div class="mi-preview-loading" *ngIf="loadingPreview">
                <div class="mi-mini-spinner"></div> Loading preview…
              </div>
              <iframe *ngIf="previewUrl && !loadingPreview"
                      [src]="previewUrl"
                      class="mi-preview-frame"
                      frameborder="0">
              </iframe>
              <div *ngIf="!previewUrl && !loadingPreview" class="mi-preview-unavail">
                <span class="material-icons">visibility_off</span>
                Preview not available for this file type
              </div>
            </div>
          </ng-container>

          <!-- ===== STEP 3: MAIL DETAILS ===== -->
          <ng-container *ngIf="currentStep === 3">
            <div class="mi-section-title">
              <span class="material-icons">info</span>
              Mail Details
            </div>
            <div class="mi-field-row">
              <div class="mi-field">
                <label class="mi-label">Mail Type</label>
                <div class="mi-readonly-chip">
                  <span class="material-icons">{{ mailTypeIcon }}</span>
                  {{ mailTypeDisplayName }}
                </div>
              </div>
              <div class="mi-field">
                <label class="mi-label">Priority <span class="mi-required">*</span></label>
                <div class="mi-priority-row">
                  <button *ngFor="let p of priorities"
                          class="mi-priority-btn"
                          [class.mi-priority-btn--selected]="priority === p.value"
                          (click)="priority = p.value">
                    {{ p.label }}
                  </button>
                </div>
              </div>
            </div>
            <div class="mi-field-row">
              <div class="mi-field mi-field--grow">
                <label class="mi-label">From — Organisation <span class="mi-required">*</span></label>
                <input class="mi-input" [(ngModel)]="fromOrganization"
                       placeholder="e.g. Ministry of Finance">
              </div>
              <div class="mi-field">
                <label class="mi-label">Sender Name</label>
                <input class="mi-input" [(ngModel)]="fromName"
                       placeholder="e.g. PS John Njoroge">
              </div>
            </div>
            <div class="mi-field-row">
              <div class="mi-field mi-field--full">
                <label class="mi-label">Subject <span class="mi-required">*</span></label>
                <input class="mi-input" [(ngModel)]="subject"
                       placeholder="e.g. Request for Budget Allocation — Q1 2026">
              </div>
            </div>
            <div class="mi-field-row">
              <div class="mi-field">
                <label class="mi-label">Date of Mail <span class="mi-required">*</span></label>
                <input class="mi-input" type="date" [(ngModel)]="dateOfMail">
              </div>
              <div class="mi-field">
                <label class="mi-label">Date Received</label>
                <input class="mi-input" type="date" [(ngModel)]="dateReceived">
              </div>
              <div class="mi-field">
                <label class="mi-label">External Reference No.</label>
                <input class="mi-input" [(ngModel)]="externalReference"
                       placeholder="e.g. MoF/FIN/2026/039">
              </div>
            </div>
            <div class="mi-field-row" *ngIf="mailType === 'OUTGOING'">
              <div class="mi-field mi-field--full">
                <label class="mi-label">In Reply To (optional)</label>
                <input class="mi-input" [(ngModel)]="inReplyToRef"
                       placeholder="Reference number of incoming mail this replies to">
              </div>
            </div>
          </ng-container>

          <!-- ===== STEP 4: CLASSIFICATION + FILE REFERENCE ===== -->
          <ng-container *ngIf="currentStep === 4">
            <div class="mi-section-title">
              <span class="material-icons">folder</span>
              File Classification
            </div>

            <!-- File Class -->
            <div class="mi-field-row">
              <div class="mi-field mi-field--grow">
                <label class="mi-label">File Class <span class="mi-required">*</span></label>
                <select class="mi-select" [(ngModel)]="selectedFileClassId" (change)="onFileClassChange()">
                  <option value="">— Select file class —</option>
                  <option *ngFor="let fc of fileClasses" [value]="fc.id">
                    {{ fc.name }} ({{ fc.code }})
                  </option>
                </select>
              </div>
            </div>
            <div class="mi-loading-row" *ngIf="loadingFileClasses">
              <div class="mi-mini-spinner"></div> Loading file classes…
            </div>

            <!-- File Reference (loads after file class selected) -->
            <ng-container *ngIf="selectedFileClassId">
              <div class="mi-section-subtitle">
                <span class="material-icons">insert_drive_file</span>
                File Reference
              </div>

              <div class="mi-loading-row" *ngIf="loadingFileRefs">
                <div class="mi-mini-spinner"></div> Loading file references…
              </div>

              <div class="mi-field-row" *ngIf="!loadingFileRefs">
                <div class="mi-field mi-field--grow">
                  <label class="mi-label">Select File <span class="mi-required">*</span></label>
                  <select class="mi-select" [(ngModel)]="selectedFileRefId" (change)="onFileRefChange()">
                    <option value="">— Select file reference —</option>
                    <option *ngFor="let ref of fileReferences" [value]="ref.id">
                      {{ ref.name }} — {{ ref.referenceNo }}
                    </option>
                  </select>
                </div>
                <div class="mi-field" style="flex:0 0 auto">
                  <label class="mi-label">Next Folio</label>
                  <div class="mi-folio-badge">
                    <div class="mi-mini-spinner" *ngIf="loadingFolio" style="width:14px;height:14px"></div>
                    <span *ngIf="!loadingFolio && folio !== null">{{ folio }}</span>
                    <span *ngIf="!loadingFolio && folio === null" style="color:#aab5c5">—</span>
                  </div>
                </div>
              </div>

              <!-- Selected file reference card -->
              <div class="mi-ref-card" *ngIf="selectedFileRef">
                <span class="material-icons" style="color:#27ae60">insert_drive_file</span>
                <div>
                  <div class="mi-ref-code">{{ selectedFileRef.referenceNo }}<span *ngIf="folio !== null"> / Folio {{ folio }}</span></div>
                  <div class="mi-ref-label">{{ selectedFileRef.name }}</div>
                </div>
              </div>

              <!-- Add new file reference -->
              <div class="mi-new-ref-row" *ngIf="!loadingFileRefs">
                <button class="mi-btn mi-btn--ghost mi-btn--sm" (click)="showNewRefForm = !showNewRefForm">
                  <span class="material-icons">add</span>
                  {{ showNewRefForm ? 'Cancel' : 'New File Reference' }}
                </button>
              </div>

              <!-- Inline new-file-reference form -->
              <div class="mi-new-ref-form" *ngIf="showNewRefForm">
                <div class="mi-field-row">
                  <div class="mi-field mi-field--grow">
                    <label class="mi-label">File Name <span class="mi-required">*</span></label>
                    <input class="mi-input" [(ngModel)]="newRefName" placeholder="e.g. TENDERS">
                  </div>
                  <div class="mi-field mi-field--grow">
                    <label class="mi-label">Reference No. <span class="mi-required">*</span></label>
                    <input class="mi-input" [(ngModel)]="newRefNo" placeholder="e.g. PROC/SCR/2026">
                  </div>
                </div>
                <div class="mi-error" *ngIf="newRefError">{{ newRefError }}</div>
                <button class="mi-btn mi-btn--primary mi-btn--sm"
                        [disabled]="!newRefName.trim() || !newRefNo.trim() || savingNewRef"
                        (click)="createNewFileRef()">
                  <span class="material-icons" *ngIf="!savingNewRef">save</span>
                  <div class="mi-mini-spinner" *ngIf="savingNewRef" style="width:14px;height:14px"></div>
                  {{ savingNewRef ? 'Saving…' : 'Save File Reference' }}
                </button>
              </div>
            </ng-container>

            <!-- Summary (shown once file ref is selected) -->
            <div class="mi-summary-card" *ngIf="selectedFileRef">
              <div class="mi-summary-title">Submission Summary</div>
              <div class="mi-summary-row"><span>Type</span><span>{{ mailTypeDisplayName }}</span></div>
              <div class="mi-summary-row"><span>Subject</span><span>{{ subject }}</span></div>
              <div class="mi-summary-row" *ngIf="fromOrganization"><span>From</span><span>{{ fromOrganization }}</span></div>
              <div class="mi-summary-row"><span>Date of Mail</span><span>{{ dateOfMail | date:'d MMM yyyy' }}</span></div>
              <div class="mi-summary-row"><span>Priority</span><span>{{ priority }}</span></div>
              <div class="mi-summary-row"><span>File Ref</span><span>{{ selectedFileRef.referenceNo }}<span *ngIf="folio !== null"> / Folio {{ folio }}</span></span></div>
            </div>
          </ng-container>

          <!-- ===== STEP 5: SEND TO ===== -->
          <ng-container *ngIf="currentStep === 5">
            <div class="mi-section-title">
              <span class="material-icons">send</span>
              Send To
            </div>

            <!-- User / Group toggle -->
            <div class="mi-recipient-toggle">
              <button class="mi-toggle-btn" [class.active]="recipientMode === 'USER'"
                      (click)="setRecipientMode('USER')">
                <span class="material-icons">person</span> Specific User
              </button>
              <button class="mi-toggle-btn" [class.active]="recipientMode === 'GROUP'"
                      (click)="setRecipientMode('GROUP')">
                <span class="material-icons">group</span> Role / Group
              </button>
            </div>

            <!-- ── USER SEARCH ── -->
            <ng-container *ngIf="recipientMode === 'USER'">
              <div class="mi-field-row">
                <div class="mi-field mi-field--full">
                  <label class="mi-label">Search User <span class="mi-required">*</span></label>
                  <div class="mi-search-wrap">
                    <span class="material-icons mi-search-icon">search</span>
                    <input class="mi-input mi-input--search"
                           [(ngModel)]="userSearchTerm"
                           (ngModelChange)="onUserSearch($event)"
                           placeholder="Type a name or username…"
                           autocomplete="off">
                    <div class="mi-mini-spinner" *ngIf="searchingUsers"></div>
                  </div>
                  <!-- Dropdown results -->
                  <div class="mi-dropdown" *ngIf="userResults.length > 0 && !selectedUser">
                    <div class="mi-dropdown-item" *ngFor="let u of userResults"
                         (click)="selectUser(u)">
                      <span class="material-icons mi-avatar">account_circle</span>
                      <div>
                        <div class="mi-dd-name">{{ u.displayName }}</div>
                        <div class="mi-dd-sub">{{ u.id }} · {{ u.email }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="mi-no-results" *ngIf="userSearchTerm.length >= 2 && userResults.length === 0 && !searchingUsers">
                    No users found for "{{ userSearchTerm }}"
                  </div>
                </div>
              </div>
              <!-- Selected user chip -->
              <div class="mi-selected-chip" *ngIf="selectedUser">
                <span class="material-icons">account_circle</span>
                <div>
                  <div class="mi-chip-name">{{ selectedUser.displayName }}</div>
                  <div class="mi-chip-sub">{{ selectedUser.id }} · {{ selectedUser.email }}</div>
                </div>
                <button class="mi-chip-remove" (click)="clearUser()">
                  <span class="material-icons">close</span>
                </button>
              </div>
            </ng-container>

            <!-- ── GROUP SEARCH ── -->
            <ng-container *ngIf="recipientMode === 'GROUP'">
              <div class="mi-field-row">
                <div class="mi-field mi-field--full">
                  <label class="mi-label">Search Group / Role <span class="mi-required">*</span></label>
                  <div class="mi-search-wrap">
                    <span class="material-icons mi-search-icon">search</span>
                    <input class="mi-input mi-input--search"
                           [(ngModel)]="groupSearchTerm"
                           (ngModelChange)="onGroupSearch($event)"
                           placeholder="Type a group or role name…"
                           autocomplete="off">
                    <div class="mi-mini-spinner" *ngIf="searchingGroups"></div>
                  </div>
                  <div class="mi-dropdown" *ngIf="groupResults.length > 0 && !selectedGroup">
                    <div class="mi-dropdown-item" *ngFor="let g of groupResults"
                         (click)="selectGroup(g)">
                      <span class="material-icons mi-avatar">group</span>
                      <div>
                        <div class="mi-dd-name">{{ g.displayName }}</div>
                        <div class="mi-dd-sub">{{ g.id }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="mi-no-results" *ngIf="groupSearchTerm.length >= 2 && groupResults.length === 0 && !searchingGroups">
                    No groups found for "{{ groupSearchTerm }}"
                  </div>
                </div>
              </div>
              <!-- Selected group chip -->
              <div class="mi-selected-chip" *ngIf="selectedGroup">
                <span class="material-icons">group</span>
                <div>
                  <div class="mi-chip-name">{{ selectedGroup.displayName }}</div>
                  <div class="mi-chip-sub">{{ selectedGroup.id }}</div>
                </div>
                <button class="mi-chip-remove" (click)="clearGroup()">
                  <span class="material-icons">close</span>
                </button>
              </div>
            </ng-container>

            <!-- ── OPTIONAL FIELDS ── -->
            <div class="mi-optional-title">Optional</div>
            <div class="mi-field-row">
              <div class="mi-field">
                <label class="mi-label">Deadline</label>
                <input class="mi-input" type="datetime-local" [(ngModel)]="deadlineAt">
              </div>
              <div class="mi-field">
                <label class="mi-label">Escalate To (if overdue)</label>
                <div class="mi-search-wrap">
                  <span class="material-icons mi-search-icon">person_search</span>
                  <input class="mi-input mi-input--search"
                         [(ngModel)]="escalateSearchTerm"
                         (ngModelChange)="onEscalateSearch($event)"
                         placeholder="Search user to escalate to…"
                         autocomplete="off">
                </div>
                <div class="mi-dropdown" *ngIf="escalateResults.length > 0 && !selectedEscalateTo">
                  <div class="mi-dropdown-item" *ngFor="let u of escalateResults"
                       (click)="selectEscalateTo(u)">
                    <span class="material-icons mi-avatar">account_circle</span>
                    <div>
                      <div class="mi-dd-name">{{ u.displayName }}</div>
                      <div class="mi-dd-sub">{{ u.id }}</div>
                    </div>
                  </div>
                </div>
                <div class="mi-selected-chip mi-selected-chip--sm" *ngIf="selectedEscalateTo">
                  <span class="material-icons" style="font-size:16px">account_circle</span>
                  <span>{{ selectedEscalateTo.displayName }}</span>
                  <button class="mi-chip-remove" (click)="selectedEscalateTo = null; escalateSearchTerm = ''">
                    <span class="material-icons">close</span>
                  </button>
                </div>
              </div>
            </div>
            <div class="mi-field-row">
              <div class="mi-field mi-field--full">
                <label class="mi-label">Instructions / Notes to Recipient</label>
                <textarea class="mi-input mi-textarea" [(ngModel)]="instructions"
                          rows="3" placeholder="Any specific instructions for the recipient…"></textarea>
              </div>
            </div>
          </ng-container>

          <!-- ===== STEP 6: SUBMIT ===== -->
          <ng-container *ngIf="currentStep === 6">
            <div class="mi-section-title">
              <span class="material-icons">task_alt</span>
              Ready to Submit
            </div>

            <!-- Final summary -->
            <div class="mi-summary-card" style="margin-bottom:16px">
              <div class="mi-summary-title">Summary</div>
              <div class="mi-summary-row"><span>Type</span><span>{{ mailTypeDisplayName }}</span></div>
              <div class="mi-summary-row"><span>Reference</span><span>{{ generatedRef }}</span></div>
              <div class="mi-summary-row"><span>Subject</span><span>{{ subject }}</span></div>
              <div class="mi-summary-row"><span>From</span><span>{{ fromOrganization }}</span></div>
              <div class="mi-summary-row"><span>Priority</span><span>{{ priority }}</span></div>
              <div class="mi-summary-row" *ngIf="selectedUser">
                <span>Recipient</span><span>{{ selectedUser.displayName }}</span>
              </div>
              <div class="mi-summary-row" *ngIf="selectedGroup">
                <span>Recipient Group</span><span>{{ selectedGroup.displayName }}</span>
              </div>
              <div class="mi-summary-row" *ngIf="deadlineAt">
                <span>Deadline</span><span>{{ deadlineAt | date:'d MMM yyyy HH:mm' }}</span>
              </div>
            </div>

            <div class="mi-submit-options">
              <!-- Option A: Save as Draft -->
              <div class="mi-submit-option" [class.mi-submit-option--selected]="submitMode === 'DRAFT'"
                   (click)="submitMode = 'DRAFT'">
                <div class="mi-submit-option-icon mi-submit-option-icon--draft">
                  <span class="material-icons">save</span>
                </div>
                <div class="mi-submit-option-body">
                  <div class="mi-submit-option-title">Save as Draft</div>
                  <div class="mi-submit-option-desc">
                    Save without starting the workflow. You can dispatch it later or delete it.
                  </div>
                </div>
                <div class="mi-submit-option-radio" [class.selected]="submitMode === 'DRAFT'"></div>
              </div>

              <!-- Option B: Submit & Dispatch Now -->
              <div class="mi-submit-option" [class.mi-submit-option--selected]="submitMode === 'DISPATCH'"
                   (click)="submitMode = 'DISPATCH'">
                <div class="mi-submit-option-icon mi-submit-option-icon--dispatch">
                  <span class="material-icons">send</span>
                </div>
                <div class="mi-submit-option-body">
                  <div class="mi-submit-option-title">Submit & Send to Inbox</div>
                  <div class="mi-submit-option-desc">
                    Start the workflow immediately. Mail goes directly to
                    <strong>{{ selectedUser?.displayName || selectedGroup?.displayName || 'recipient' }}</strong>'s inbox.
                  </div>
                </div>
                <div class="mi-submit-option-radio" [class.selected]="submitMode === 'DISPATCH'"></div>
              </div>
            </div>

            <div class="mi-error" *ngIf="submitError">
              <span class="material-icons">error_outline</span> {{ submitError }}
            </div>
            <div class="mi-success" *ngIf="submitSuccess">
              <span class="material-icons">check_circle</span> {{ submitSuccess }}
            </div>
          </ng-container>

          </div><!-- /mi-step-content -->

          <!-- ===== NAVIGATION BUTTONS ===== -->
          <div class="mi-nav-row">
            <button class="mi-btn mi-btn--ghost" (click)="prevStep()" *ngIf="currentStep > 1">
              <span class="material-icons">arrow_back</span> Previous
            </button>
            <div style="flex:1"></div>
            <!-- Steps 1–5: Next -->
            <button class="mi-btn mi-btn--primary"
                    *ngIf="currentStep < 6 && !(currentStep === 1 && mailType === 'INTERNAL_MEMO')"
                    [disabled]="!canProceed()"
                    (click)="nextStep()">
              Next <span class="material-icons">arrow_forward</span>
            </button>
            <!-- Internal Memo: step 1 → template picker -->
            <button class="mi-btn mi-btn--primary"
                    *ngIf="currentStep === 1 && mailType === 'INTERNAL_MEMO'"
                    (click)="goToTemplatePicker()">
              Pick Template <span class="material-icons">edit_note</span>
            </button>
            <!-- Step 6: final submit -->
            <button class="mi-btn mi-btn--submit"
                    *ngIf="currentStep === 6"
                    [disabled]="saving || !submitMode"
                    (click)="submitForm()">
              <span class="material-icons">{{ submitMode === 'DRAFT' ? 'save' : 'send' }}</span>
              <span *ngIf="!saving">{{ submitMode === 'DRAFT' ? 'Save Draft' : 'Submit & Send' }}</span>
              <span *ngIf="saving">Processing…</span>
            </button>
          </div>

        </main>
      </div>
    </div>
  `,
  styles: [`
    .mi-container {
      display: flex; flex-direction: column;
      height: 100%; background: #f0f4fa; font-family: 'DM Sans', sans-serif;
    }
    .mi-topbar {
      background: #1B3A6B; padding: 0 20px;
      height: 52px; display: flex; align-items: center; gap: 12px;
      flex-shrink: 0; border-bottom: 1px solid rgba(255,255,255,0.1);
    }
    .mi-back-btn {
      background: rgba(255,255,255,0.1); border: none; cursor: pointer;
      border-radius: 6px; width: 32px; height: 32px;
      display: flex; align-items: center; justify-content: center; color: white;
    }
    .mi-back-btn:hover { background: rgba(255,255,255,0.2); }
    .mi-topbar-info { flex: 1; }
    .mi-topbar-title { color: white; font-weight: 700; font-size: 14px; display: block; }
    .mi-topbar-sub { color: rgba(255,255,255,0.5); font-size: 11px; }
    .mi-topbar-actions { display: flex; gap: 8px; }
    .mi-body { display: flex; flex: 1; overflow: hidden; }
    .mi-steps {
      width: 220px; background: #f8faff; border-right: 1px solid #d8e4f0;
      padding: 24px 0; flex-shrink: 0; overflow-y: auto;
    }
    .mi-step {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 16px; cursor: default; transition: background 0.15s;
    }
    .mi-step--done { cursor: pointer; }
    .mi-step--done:hover { background: rgba(43,92,184,0.05); }
    .mi-step-circle {
      width: 28px; height: 28px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 12px; font-weight: 700; flex-shrink: 0;
      background: #e8edf5; border: 2px solid #c8d6e8; color: #8a9bb0;
    }
    .mi-step--active .mi-step-circle { background: #2B5CB8; border-color: #2B5CB8; color: white; }
    .mi-step--done .mi-step-circle  { background: #0D7A5F; border-color: #0D7A5F; color: white; }
    .mi-step-title { font-size: 12px; font-weight: 600; color: #1B3A6B; }
    .mi-step--active .mi-step-title { color: #2B5CB8; }
    .mi-step-sub { font-size: 10px; color: #8a9bb0; }
    .mi-step-connector { width: 2px; height: 14px; background: #d8e4f0; margin-left: 29px; }
    .mi-content {
      flex: 1; min-height: 0;
      padding: 28px 32px 0; overflow: hidden;
      background: white; display: flex; flex-direction: column;
    }
    .mi-section-title {
      display: flex; align-items: center; gap: 8px;
      font-size: 15px; font-weight: 700; color: #1B3A6B;
      margin-bottom: 20px; padding-bottom: 10px;
      border-bottom: 2px solid #f0f4fa;
    }
    .mi-section-title .material-icons { color: #0D7A5F; font-size: 20px; }
    .mi-type-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; margin-bottom: 20px; }
    .mi-type-card {
      border: 2px solid #d8e4f0; border-radius: 10px;
      padding: 20px 16px; text-align: center; cursor: pointer;
      transition: all 0.15s; position: relative; background: white;
    }
    .mi-type-card:hover { border-color: #2B5CB8; box-shadow: 0 4px 14px rgba(43,92,184,0.12); }
    .mi-type-card--selected { border-color: #2B5CB8; background: rgba(43,92,184,0.04); }
    .mi-type-icon { font-size: 32px !important; color: #aab5c5; display: block; margin-bottom: 10px; }
    .mi-type-card--selected .mi-type-icon { color: #2B5CB8; }
    .mi-type-name { font-size: 13px; font-weight: 700; color: #1B3A6B; margin-bottom: 4px; }
    .mi-type-sub { font-size: 11px; color: #8a9bb0; }
    .mi-type-check { position: absolute; top: 8px; right: 8px; color: #2B5CB8; }
    .mi-type-check .material-icons { font-size: 20px; }
    .mi-info-box {
      background: rgba(43,92,184,0.06); border: 1px solid rgba(43,92,184,0.2);
      border-radius: 6px; padding: 10px 14px;
      display: flex; align-items: flex-start; gap: 8px;
      font-size: 12px; color: #2B5CB8;
    }
    .mi-info-box .material-icons { font-size: 16px; flex-shrink: 0; margin-top: 1px; }
    .mi-upload-zone {
      border: 2px dashed #c8d6e8; border-radius: 10px;
      padding: 48px 32px; text-align: center;
      background: #f8faff; cursor: pointer; transition: all 0.15s; margin-bottom: 16px;
    }
    .mi-upload-zone:hover, .mi-upload-zone--drag { border-color: #2B5CB8; background: rgba(43,92,184,0.04); }
    .mi-upload-icon { font-size: 48px !important; color: #aab5c5; display: block; margin-bottom: 10px; }
    .mi-upload-title { font-size: 14px; font-weight: 600; color: #1B3A6B; margin-bottom: 4px; }
    .mi-upload-sub { font-size: 12px; color: #8a9bb0; }
    .mi-upload-formats { display: flex; justify-content: center; gap: 6px; margin-top: 12px; }
    .mi-fmt {
      background: white; border: 1px solid #d8e4f0; border-radius: 4px;
      padding: 2px 8px; font-size: 10px; font-weight: 700; color: #6b7280;
    }
    .mi-upload-progress {
      display: flex; align-items: center; gap: 10px;
      padding: 16px; background: #f8faff; border-radius: 8px;
      font-size: 13px; color: #555; margin-bottom: 16px;
    }
    .mi-upload-spinner {
      width: 20px; height: 20px; border: 2px solid #e0e8f4;
      border-top-color: #2B5CB8; border-radius: 50%;
      animation: mi-spin 0.7s linear infinite;
    }
    .mi-file-card {
      display: flex; align-items: center; gap: 12px;
      padding: 12px 16px; background: rgba(13,122,95,0.05);
      border: 1px solid rgba(13,122,95,0.2); border-radius: 8px; margin-bottom: 16px;
    }
    .mi-file-icon { font-size: 28px !important; color: #e53e3e; }
    .mi-file-icon--pdf { color: #e53e3e; }
    .mi-file-info { flex: 1; }
    .mi-file-name { font-size: 13px; font-weight: 600; color: #1B3A6B; }
    .mi-file-meta { font-size: 11px; color: #8a9bb0; }
    .mi-file-remove { background: none; border: none; cursor: pointer; color: #aab5c5; padding: 4px; border-radius: 4px; }
    .mi-file-remove:hover { color: #e53e3e; background: #fee2e2; }
    .mi-field-row { display: flex; gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
    .mi-field { display: flex; flex-direction: column; gap: 5px; min-width: 160px; flex: 1; }
    .mi-field--grow { flex: 2; }
    .mi-field--full { flex: 3; width: 100%; }
    .mi-label { font-size: 11px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.4px; }
    .mi-required { color: #e53e3e; }
    .mi-input, .mi-select {
      border: 1px solid #d8e4f0; border-radius: 6px;
      padding: 8px 12px; font-size: 13px; color: #1a1a2e;
      background: white; width: 100%; transition: border-color 0.15s;
    }
    .mi-input:focus, .mi-select:focus {
      outline: none; border-color: #2B5CB8;
      box-shadow: 0 0 0 3px rgba(43,92,184,0.1);
    }
    .mi-textarea { resize: vertical; min-height: 72px; font-family: inherit; }
    .mi-readonly-chip {
      display: inline-flex; align-items: center; gap: 6px;
      background: #f0f4fa; border: 1px solid #d8e4f0;
      border-radius: 6px; padding: 8px 12px; font-size: 13px; color: #1B3A6B; font-weight: 600;
    }
    .mi-readonly-chip .material-icons { font-size: 16px; color: #6b7280; }
    .mi-priority-row { display: flex; gap: 6px; }
    .mi-priority-btn {
      border: 2px solid #d8e4f0; border-radius: 6px;
      padding: 6px 12px; font-size: 11px; font-weight: 700;
      cursor: pointer; background: white; color: #6b7280; transition: all 0.12s;
    }
    .mi-priority-btn--selected { border-color: #2B5CB8; color: #2B5CB8; background: rgba(43,92,184,0.06); }
    .mi-ref-card {
      display: flex; align-items: center; gap: 12px;
      background: rgba(13,122,95,0.06); border: 1px solid rgba(13,122,95,0.2);
      border-radius: 8px; padding: 12px 16px; margin: 14px 0;
    }
    .mi-ref-code { font-family: monospace; font-size: 16px; font-weight: 700; color: #0D7A5F; }
    .mi-ref-label { font-size: 11px; color: #8a9bb0; }
    .mi-loading-row { display: flex; align-items: center; gap: 8px; color: #8a9bb0; font-size: 12px; margin: 8px 0; }
    .mi-mini-spinner {
      width: 14px; height: 14px; border: 2px solid #d8e4f0;
      border-top-color: #2B5CB8; border-radius: 50%;
      animation: mi-spin 0.7s linear infinite;
    }
    .mi-summary-card { background: #f8faff; border: 1px solid #d8e4f0; border-radius: 8px; padding: 14px 16px; margin-top: 6px; }
    .mi-summary-title { font-size: 10px; font-weight: 700; color: #8a9bb0; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 10px; }
    .mi-summary-row { display: flex; justify-content: space-between; align-items: baseline; font-size: 12px; margin-bottom: 6px; gap: 12px; }
    .mi-summary-row span:first-child { color: #8a9bb0; flex-shrink: 0; }
    .mi-summary-row span:last-child { color: #1B3A6B; font-weight: 500; text-align: right; }

    /* ── Step 5: Send To ── */
    .mi-recipient-toggle { display: flex; gap: 8px; margin-bottom: 20px; }
    .mi-toggle-btn {
      display: flex; align-items: center; gap: 6px;
      padding: 8px 18px; border-radius: 6px; border: 2px solid #d8e4f0;
      background: white; font-size: 13px; font-weight: 600; color: #6b7280; cursor: pointer;
      transition: all 0.15s;
    }
    .mi-toggle-btn .material-icons { font-size: 17px; }
    .mi-toggle-btn.active { border-color: #2B5CB8; color: #2B5CB8; background: rgba(43,92,184,0.06); }
    .mi-search-wrap { position: relative; display: flex; align-items: center; }
    .mi-search-icon { position: absolute; left: 10px; font-size: 18px !important; color: #aab5c5; pointer-events: none; }
    .mi-input--search { padding-left: 36px !important; }
    .mi-dropdown {
      border: 1px solid #d8e4f0; border-radius: 8px; background: white;
      box-shadow: 0 4px 16px rgba(0,0,0,0.1); margin-top: 4px;
      max-height: 220px; overflow-y: auto; position: relative; z-index: 10;
    }
    .mi-dropdown-item {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 14px; cursor: pointer; transition: background 0.1s;
    }
    .mi-dropdown-item:hover { background: #f0f4fa; }
    .mi-avatar { font-size: 28px !important; color: #aab5c5; }
    .mi-dd-name { font-size: 13px; font-weight: 600; color: #1B3A6B; }
    .mi-dd-sub  { font-size: 11px; color: #8a9bb0; }
    .mi-no-results { font-size: 12px; color: #8a9bb0; padding: 8px 0; }
    .mi-selected-chip {
      display: flex; align-items: center; gap: 10px;
      background: rgba(43,92,184,0.06); border: 1px solid rgba(43,92,184,0.2);
      border-radius: 8px; padding: 10px 14px; margin-top: 8px;
    }
    .mi-selected-chip .material-icons { font-size: 28px; color: #2B5CB8; }
    .mi-selected-chip--sm { padding: 6px 12px; margin-top: 4px; }
    .mi-selected-chip--sm .material-icons { font-size: 16px; }
    .mi-chip-name { font-size: 13px; font-weight: 600; color: #1B3A6B; }
    .mi-chip-sub  { font-size: 11px; color: #8a9bb0; }
    .mi-chip-remove { margin-left: auto; background: none; border: none; cursor: pointer; color: #aab5c5; border-radius: 4px; padding: 2px; }
    .mi-chip-remove:hover { color: #e53e3e; background: #fee2e2; }
    .mi-optional-title { font-size: 11px; font-weight: 700; color: #8a9bb0; text-transform: uppercase; letter-spacing: 0.6px; margin: 18px 0 10px; }

    /* ── Step 6 submit options ── */
    .mi-submit-options { display: flex; flex-direction: column; gap: 12px; margin-bottom: 20px; }
    .mi-submit-option {
      display: flex; align-items: flex-start; gap: 14px;
      border: 2px solid #d8e4f0; border-radius: 10px;
      padding: 16px; cursor: pointer; transition: all 0.15s;
    }
    .mi-submit-option:hover { border-color: #2B5CB8; }
    .mi-submit-option--selected { border-color: #2B5CB8; background: rgba(43,92,184,0.04); }
    .mi-submit-option-icon {
      width: 44px; height: 44px; border-radius: 10px;
      display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    }
    .mi-submit-option-icon--draft    { background: rgba(107,114,128,0.12); color: #6b7280; }
    .mi-submit-option-icon--dispatch { background: rgba(13,122,95,0.12);   color: #0D7A5F; }
    .mi-submit-option-icon .material-icons { font-size: 22px; }
    .mi-submit-option-body { flex: 1; }
    .mi-submit-option-title { font-size: 13px; font-weight: 700; color: #1B3A6B; margin-bottom: 4px; }
    .mi-submit-option-desc  { font-size: 12px; color: #6b7280; line-height: 1.5; }
    .mi-submit-option-radio {
      width: 18px; height: 18px; border-radius: 50%;
      border: 2px solid #d8e4f0; flex-shrink: 0; margin-top: 4px; transition: all 0.15s;
    }
    .mi-submit-option-radio.selected { background: #2B5CB8; border-color: #2B5CB8; }

    /* ── Step content scrolls, nav stays pinned ─────────────────────────── */
    .mi-step-content {
      flex: 1; min-height: 0; overflow-y: auto; padding-bottom: 16px;
    }
    /* ── Nav row ── */
    .mi-nav-row {
      display: flex; align-items: center; gap: 8px;
      padding: 14px 0 18px; border-top: 1px solid #f0f4fa;
      flex-shrink: 0; background: white;
    }
    .mi-btn {
      display: inline-flex; align-items: center; gap: 6px;
      border-radius: 6px; padding: 8px 16px;
      font-size: 13px; font-weight: 600; cursor: pointer;
      border: none; transition: all 0.15s;
    }
    .mi-btn .material-icons { font-size: 16px; }
    .mi-btn:disabled { opacity: 0.45; cursor: not-allowed; }
    .mi-btn--ghost   { background: white; color: #6b7280; border: 1px solid #d8e4f0; }
    .mi-btn--ghost:hover:not(:disabled) { background: #f0f4fa; }
    .mi-btn--primary { background: #1B3A6B; color: white; }
    .mi-btn--primary:hover:not(:disabled) { background: #2B5CB8; }
    .mi-btn--submit  { background: #0D7A5F; color: white; padding: 10px 20px; }
    .mi-btn--submit:hover:not(:disabled) { background: #0a6349; }

    .mi-error   { display: flex; align-items: center; gap: 8px; background: #fee2e2; color: #991b1b; border-radius: 6px; padding: 10px 14px; font-size: 12px; margin-bottom: 12px; }
    .mi-success { display: flex; align-items: center; gap: 8px; background: rgba(13,122,95,0.08); color: #0D7A5F; border-radius: 6px; padding: 10px 14px; font-size: 12px; margin-bottom: 12px; }
    .mi-error .material-icons, .mi-success .material-icons { font-size: 16px; }

    @keyframes mi-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

    /* ── Upload preview ─────────────────────────────────────────────────── */
    .mi-preview-wrap {
      margin-top: 16px; border: 1px solid #d8e4f0; border-radius: 8px;
      overflow: hidden; display: flex; flex-direction: column;
    }
    .mi-preview-label {
      padding: 8px 14px; font-size: 11px; font-weight: 600; color: #5a7a99;
      background: #eef4fb; border-bottom: 1px solid #d8e4f0;
      letter-spacing: 0.04em; text-transform: uppercase;
      display: flex; align-items: center; gap: 6px; flex-shrink: 0;
    }
    .mi-preview-frame {
      width: 100%;
      height: 600px;
      display: block; border: none;
    }
    .mi-preview-loading {
      display: flex; align-items: center; gap: 10px;
      padding: 32px 16px; color: #5a7a99; font-size: 13px;
    }
    .mi-preview-unavail {
      display: flex; align-items: center; gap: 8px;
      padding: 32px 16px; color: #8a9bb0; font-size: 13px;
    }

    /* ── File reference section ─────────────────────────────────────────── */
    .mi-section-subtitle {
      display: flex; align-items: center; gap: 6px;
      font-size: 12px; font-weight: 700; color: #1B3A6B;
      text-transform: uppercase; letter-spacing: 0.06em;
      margin: 18px 0 10px; padding-bottom: 6px; border-bottom: 1px solid #d8e4f0;
    }
    .mi-folio-badge {
      display: flex; align-items: center; justify-content: center;
      min-width: 72px; height: 36px;
      background: #f0f6ff; border: 1px solid #c8d6e8; border-radius: 6px;
      font-size: 18px; font-weight: 700; color: #1B3A6B; font-family: monospace;
    }
    .mi-new-ref-row { margin-top: 10px; }
    .mi-btn--sm { font-size: 12px; padding: 6px 12px; gap: 4px; height: auto; }
    .mi-btn--sm .material-icons { font-size: 14px; }
    .mi-new-ref-form {
      margin-top: 10px; padding: 14px;
      background: #f0f6ff; border: 1px dashed #a8c4e0; border-radius: 8px;
    }
  `]
})
export class MailInitiateComponent implements OnInit, OnDestroy {

  currentStep: StepId = 1;
  editingDraftId: string | null = null;

  readonly steps = [
    { id: 1 as StepId, title: 'Mail Type',       subtitle: 'Choose type'     },
    { id: 2 as StepId, title: 'Upload Document', subtitle: 'Attach file'     },
    { id: 3 as StepId, title: 'Mail Details',    subtitle: 'Fill metadata'   },
    { id: 4 as StepId, title: 'Classification',  subtitle: 'File class & ref'},
    { id: 5 as StepId, title: 'Send To',         subtitle: 'Select recipient'},
    { id: 6 as StepId, title: 'Submit',          subtitle: 'Draft or send'   },
  ];

  // ── Step 1 ───────────────────────────────────────────────────────────────
  mailType: MailType = 'INCOMING';

  readonly mailTypeOptions = [
    { value: 'INCOMING'      as MailType, label: 'Incoming Mail',  icon: 'download',  description: 'Physical or digital mail received from an external party' },
    { value: 'OUTGOING'      as MailType, label: 'Outgoing Mail',  icon: 'upload',    description: 'A letter or response to be sent to an external party' },
    { value: 'INTERNAL_MEMO' as MailType, label: 'Internal Memo',  icon: 'edit_note', description: 'Internal communication — uses an OnlyOffice template' },
  ];

  // ── Step 2 ───────────────────────────────────────────────────────────────
  isDragging = false;
  uploading = false;
  uploadError = '';
  uploadedFileName = '';
  uploadedFile: { nodeId: string; fileName: string; fileSize: number } | null = null;

  get isPdf(): boolean { return this.uploadedFile?.fileName?.toLowerCase().endsWith('.pdf') ?? false; }

  // ── Step 3 ───────────────────────────────────────────────────────────────
  fromOrganization = '';
  fromName = '';
  fromEmail = '';
  subject = '';
  dateOfMail = '';
  dateReceived = '';
  externalReference = '';
  inReplyToRef = '';
  priority: Priority = 'NORMAL';

  readonly priorities = [
    { value: 'NORMAL'       as Priority, label: 'Normal'       },
    { value: 'URGENT'       as Priority, label: 'Urgent'       },
    { value: 'CONFIDENTIAL' as Priority, label: 'Confidential' },
  ];

  // ── Step 4 ───────────────────────────────────────────────────────────────
  fileClasses: FileClassDto[] = [];
  loadingFileClasses = false;
  selectedFileClassId: number | '' = '';
  generatedRef = '';
  generatingRef = false;

  // ── Step 4b: File Reference ──────────────────────────────────────────
  fileReferences: FileReferenceDto[] = [];
  loadingFileRefs = false;
  selectedFileRefId: number | '' = '';
  selectedFileRef: FileReferenceDto | null = null;
  folio: number | null = null;       // auto-assigned by backend — read-only
  loadingFolio = false;

  // New-file-reference inline form
  showNewRefForm = false;
  newRefName = '';
  newRefNo = '';
  savingNewRef = false;
  newRefError = '';

  // ── Step 2b: Upload preview ──────────────────────────────────────────
  previewUrl: SafeResourceUrl | null = null;
  loadingPreview = false;

  // ── Step 5: Send To ──────────────────────────────────────────────────────
  recipientMode: RecipientMode = 'USER';

  // User search
  userSearchTerm = '';
  userResults: AlfrescoUser[] = [];
  searchingUsers = false;
  selectedUser: AlfrescoUser | null = null;

  // Group search
  groupSearchTerm = '';
  groupResults: AlfrescoGroup[] = [];
  searchingGroups = false;
  selectedGroup: AlfrescoGroup | null = null;

  // Escalation
  escalateSearchTerm = '';
  escalateResults: AlfrescoUser[] = [];
  selectedEscalateTo: AlfrescoUser | null = null;

  // Optional dispatch fields
  deadlineAt = '';
  instructions = '';

  // ── Step 6 ───────────────────────────────────────────────────────────────
  submitMode: 'DRAFT' | 'DISPATCH' | null = 'DRAFT';
  saving = false;
  submitError = '';
  submitSuccess = '';

  private userSearch$  = new Subject<string>();
  private groupSearch$ = new Subject<string>();
  private escSearch$   = new Subject<string>();
  private destroy$     = new Subject<void>();

  constructor(
    private router: Router,
    private route:  ActivatedRoute,
    private mailService: MailWorkflowService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.dateReceived = new Date().toISOString().split('T')[0];
    this.wireSearchStreams();

    // Pre-fill Sender Name with current user's display name
    this.mailService.getCurrentUser().pipe(takeUntil(this.destroy$)).subscribe({
      next: u => { if (!this.fromName) this.fromName = u.displayName; },
      error: () => {}
    });

    // Check if we are editing an existing draft
    const draftId = this.route.snapshot.paramMap.get('draftId');
    if (draftId) {
      this.editingDraftId = draftId;
      this.loadDraftForEdit(draftId);
    }
  }

  private loadDraftForEdit(draftId: string): void {
    this.mailService.getMyDrafts().pipe(takeUntil(this.destroy$)).subscribe({
      next: drafts => {
        const draft = drafts.find(d => (d as any).draftId === draftId || d.taskId === draftId);
        if (!draft) return;
        const d = draft as any;

        // Step 1 — mail type
        this.mailType = d.mailType ?? 'INCOMING';

        // Step 2 — document already uploaded; reconstruct uploadedFile stub so step shows done
        if (draft.documentNodeId) {
          this.uploadedFile = {
            nodeId:   draft.documentNodeId,
            fileName: draft.subject || draft.referenceNumber || 'document',
            fileSize: 0
          };
          this.uploadedFileName = this.uploadedFile.fileName;
        }

        // Step 3 — mail details
        this.fromOrganization = d.fromOrganization ?? '';
        this.fromName         = d.fromName         ?? '';
        this.fromEmail        = d.fromEmail        ?? '';
        this.subject          = draft.subject      ?? '';
        this.priority         = d.priority         ?? 'NORMAL';
        if (d.dateOfMail)     this.dateOfMail      = d.dateOfMail;

        // Step 4 — classification
        // /my-drafts returns fileClassCode and referenceNumber but not fileClassId.
        // Load file classes then match by code to get the numeric id for the dropdown.
        this.generatedRef = draft.referenceNumber ?? '';
        if (draft.fileClassCode) {
          this.loadingFileClasses = true;
          this.mailService.getFileClasses().pipe(takeUntil(this.destroy$)).subscribe({
            next: fc => {
              this.fileClasses = fc;
              this.loadingFileClasses = false;
              const match = fc.find(f => f.code === draft.fileClassCode || f.name === draft.fileClassCode);
              if (match) this.selectedFileClassId = match.id;
            },
            error: () => { this.loadingFileClasses = false; }
          });
        }

        // Step 5 — recipient (only present if user filled it before saving draft)
        if (d.dispatchTo) {
          this.selectedUser = {
            id: d.dispatchTo,
            displayName: d.dispatchToName ?? d.dispatchTo,
            email: d.dispatchToEmail ?? ''
          };
          this.userSearchTerm = this.selectedUser.displayName;
        }
        if (d.dispatchToGroup) {
          this.selectedGroup = { id: d.dispatchToGroup, displayName: d.dispatchToName ?? d.dispatchToGroup };
          this.recipientMode = 'GROUP';
          this.groupSearchTerm = this.selectedGroup.displayName;
        }
        if (d.deadlineAt)   this.deadlineAt   = d.deadlineAt;
        if (d.instructions) this.instructions = d.instructions;

        // Jump straight to Step 6 — all data is populated
        this.currentStep = 6;
      },
      error: () => { /* silently ignore — user can fill in manually */ }
    });
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  // ── Computed helpers ─────────────────────────────────────────────────────

  get mailTypeDisplayName(): string { return this.mailTypeOptions.find(t => t.value === this.mailType)?.label ?? ''; }
  get mailTypeIcon():        string { return this.mailTypeOptions.find(t => t.value === this.mailType)?.icon  ?? 'mail'; }

  // ── Navigation ───────────────────────────────────────────────────────────

  canProceed(): boolean {
    switch (this.currentStep) {
      case 1: return !!this.mailType;
      case 2: return !!this.uploadedFile || this.mailType === 'INTERNAL_MEMO';
      case 3: return !!(this.fromOrganization && this.subject && this.dateOfMail);
      case 4: return !!(this.selectedFileClassId && this.selectedFileRef);
      case 5: return !!(this.selectedUser || this.selectedGroup);  // recipient required
      default: return true;
    }
  }

  selectMailType(type: MailType): void { this.mailType = type; }

  nextStep(): void {
    if (!this.canProceed()) return;
    if (this.currentStep === 1 && this.mailType === 'INTERNAL_MEMO') { this.goToTemplatePicker(); return; }
    if (this.currentStep === 3 && this.fileClasses.length === 0) this.loadFileClasses();
    if (this.currentStep < 6) this.currentStep = (this.currentStep + 1) as StepId;
  }

  prevStep(): void { if (this.currentStep > 1) this.currentStep = (this.currentStep - 1) as StepId; }
  goToStep(step: StepId): void { this.currentStep = step; }
  goBack(): void { this.router.navigate(['/workflow']); }
  goToTemplatePicker(): void { this.router.navigate(['/mail-workflow/memo/template']); }

  // ── Step 2: File upload ──────────────────────────────────────────────────

  onDragOver(event: DragEvent): void { event.preventDefault(); this.isDragging = true; }

  onDrop(event: DragEvent): void {
    event.preventDefault(); this.isDragging = false;
    const file = event.dataTransfer?.files[0];
    if (file) this.uploadFile(file);
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) this.uploadFile(file);
  }

  private uploadFile(file: File): void {
    this.uploadError = ''; this.uploading = true; this.uploadedFileName = file.name;
    this.mailService.uploadDocument(file).pipe(takeUntil(this.destroy$)).subscribe({
      next: (r) => {
        this.uploading = false;
        this.uploadedFile = r;
        this.fetchUploadPreview(r.nodeId);
      },
      error: (e) => { this.uploading = false; this.uploadError = e?.error?.message ?? 'Upload failed.'; }
    });
  }

  removeFile(): void { this.uploadedFile = null; this.uploadError = ''; this.previewUrl = null; }

  private fetchUploadPreview(nodeId: string): void {
    this.loadingPreview = true;
    this.mailService.getContentBlob(nodeId).pipe(takeUntil(this.destroy$)).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        this.loadingPreview = false;
      },
      error: () => { this.loadingPreview = false; }
    });
  }

  formatSize(bytes: number): string {
    if (!bytes) return '';
    return bytes > 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
  }

  // ── Step 4: File classification ──────────────────────────────────────────

  private loadFileClasses(): void {
    if (this.fileClasses.length > 0) return;
    this.loadingFileClasses = true;
    this.mailService.getFileClasses().pipe(takeUntil(this.destroy$)).subscribe({
      next: (c) => { this.fileClasses = c; this.loadingFileClasses = false; },
      error: ()  => { this.loadingFileClasses = false; }
    });
  }

  private loadFileReferences(fileClassId: number): void {
    this.loadingFileRefs = true;
    this.mailService.getFileReferences(fileClassId).pipe(takeUntil(this.destroy$)).subscribe({
      next: refs => { this.fileReferences = refs; this.loadingFileRefs = false; },
      error: ()   => { this.loadingFileRefs = false; }
    });
  }

  onFileRefChange(): void {
    if (!this.selectedFileRefId) { this.selectedFileRef = null; this.folio = null; return; }
    this.selectedFileRef = this.fileReferences.find(r => r.id === Number(this.selectedFileRefId)) ?? null;
    if (this.selectedFileRef) {
      this.folio = null;
      this.loadingFolio = true;
      this.mailService.getNextFolio(this.selectedFileRef.id).pipe(takeUntil(this.destroy$)).subscribe({
        next: n => { this.folio = n; this.loadingFolio = false; },
        error: () => { this.loadingFolio = false; }
      });
    }
  }

  createNewFileRef(): void {
    if (!this.newRefName.trim() || !this.newRefNo.trim() || !this.selectedFileClassId) return;
    this.savingNewRef = true; this.newRefError = '';
    const req: CreateFileReferenceRequest = {
      fileClassId: Number(this.selectedFileClassId),
      name: this.newRefName.trim(),
      referenceNo: this.newRefNo.trim()
    };
    this.mailService.createFileReference(req).pipe(takeUntil(this.destroy$)).subscribe({
      next: ref => {
        this.fileReferences = [...this.fileReferences, ref];
        this.selectedFileRefId = ref.id;
        this.selectedFileRef = ref;
        this.folio = 1;   // new file always starts at folio 1
        this.showNewRefForm = false;
        this.newRefName = ''; this.newRefNo = '';
        this.savingNewRef = false;
      },
      error: (e) => { this.newRefError = e?.error?.message ?? 'Failed to create file reference'; this.savingNewRef = false; }
    });
  }

  onFileClassChange(): void {
    if (!this.selectedFileClassId) {
      this.generatedRef = '';
      this.fileReferences = []; this.selectedFileRefId = '';
      this.selectedFileRef = null; this.folio = null; this.showNewRefForm = false;
      return;
    }
    // Reset file reference when class changes
    this.fileReferences = []; this.selectedFileRefId = '';
    this.selectedFileRef = null; this.folio = null; this.showNewRefForm = false;
    this.loadFileReferences(Number(this.selectedFileClassId));
    // Still generate the document reference number (CODE/YEAR/MM/SEQ)
    this.generatedRef = ''; this.generatingRef = true;
    this.mailService.generateRefNumber(Number(this.selectedFileClassId)).pipe(takeUntil(this.destroy$)).subscribe({
      next: (r) => { this.generatedRef = r; this.generatingRef = false; },
      error: ()  => { this.generatingRef = false; }
    });
  }

  // ── Step 5: Recipient search ─────────────────────────────────────────────

  setRecipientMode(mode: RecipientMode): void {
    this.recipientMode = mode;
    this.clearUser(); this.clearGroup();
  }

  onUserSearch(term: string):    void { this.userSearch$.next(term); }
  onGroupSearch(term: string):   void { this.groupSearch$.next(term); }
  onEscalateSearch(term: string):void { this.escSearch$.next(term); }

  selectUser(u: AlfrescoUser):       void { this.selectedUser = u;      this.userResults = [];     this.userSearchTerm = u.displayName; }
  selectGroup(g: AlfrescoGroup):     void { this.selectedGroup = g;     this.groupResults = [];    this.groupSearchTerm = g.displayName; }
  selectEscalateTo(u: AlfrescoUser): void { this.selectedEscalateTo = u; this.escalateResults = []; this.escalateSearchTerm = u.displayName; }
  clearUser():  void { this.selectedUser  = null; this.userSearchTerm  = ''; this.userResults  = []; }
  clearGroup(): void { this.selectedGroup = null; this.groupSearchTerm = ''; this.groupResults = []; }

  private wireSearchStreams(): void {
    // User search — reuses the same /api/contracts/workflow/users/search endpoint
    this.userSearch$.pipe(
      debounceTime(300), distinctUntilChanged(),
      switchMap(term => {
        if (term.length < 2) { this.userResults = []; return []; }
        this.searchingUsers = true;
        return this.mailService.searchUsers(term);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (users) => { this.userResults = users; this.searchingUsers = false; },
      error: ()      => { this.searchingUsers = false; }
    });

    // Group search
    this.groupSearch$.pipe(
      debounceTime(300), distinctUntilChanged(),
      switchMap(term => {
        if (term.length < 2) { this.groupResults = []; return []; }
        this.searchingGroups = true;
        return this.mailService.searchGroups(term);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (groups) => { this.groupResults = groups; this.searchingGroups = false; },
      error: ()       => { this.searchingGroups = false; }
    });

    // Escalate-to user search
    this.escSearch$.pipe(
      debounceTime(300), distinctUntilChanged(),
      switchMap(term => term.length >= 2 ? this.mailService.searchUsers(term) : []),
      takeUntil(this.destroy$)
    ).subscribe({ next: (u) => { this.escalateResults = u; } });
  }

  // ── Step 6: Submit ───────────────────────────────────────────────────────

  saveDraft(): void {
    if (!this.uploadedFile) return;
    this.saving = true; this.submitError = '';
    this.mailService.saveDraft(this.buildRequest()).pipe(takeUntil(this.destroy$)).subscribe({
      next: r => {
        this.saving = false;
        if (!this.editingDraftId) this.editingDraftId = r.draftId;
        this.submitSuccess = 'Draft saved.';
        setTimeout(() => this.router.navigate(['/workflow']), 1500);
      },
      error: (e) => { this.saving = false; this.submitError = e?.error?.message ?? 'Could not save draft.'; }
    });
  }

  submitForm(): void {
    if (!this.submitMode) return;
    this.saving = true; this.submitError = '';

    if (this.submitMode === 'DRAFT') {
      // Save (or re-save) as draft
      this.mailService.saveDraft(this.buildRequest()).pipe(takeUntil(this.destroy$)).subscribe({
        next: r => {
          this.saving = false;
          if (!this.editingDraftId) this.editingDraftId = r.draftId;
          this.submitSuccess = 'Saved as draft. Redirecting…';
          setTimeout(() => this.router.navigate(['/workflow']), 1500);
        },
        error: (e) => { this.saving = false; this.submitError = e?.error?.message ?? 'Could not save draft.'; }
      });
    } else {
      // DISPATCH — if we have an existing draftId, submit it directly
      if (this.editingDraftId) {
        this.mailService.submitDraft(this.editingDraftId).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.saving = false;
            this.submitSuccess = 'Submitted! Mail workflow started.';
            setTimeout(() => this.router.navigate(['/workflow']), 1500);
          },
          error: (e) => { this.saving = false; this.submitError = e?.error?.message ?? 'Submission failed.'; }
        });
      } else {
        // New mail — start workflow directly
        this.mailService.startWorkflow(this.buildRequest()).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.saving = false;
            this.submitSuccess = 'Submitted! Mail sent to recipient\'s inbox.';
            setTimeout(() => this.router.navigate(['/workflow']), 1500);
          },
          error: (e) => { this.saving = false; this.submitError = e?.error?.message ?? 'Submission failed.'; }
        });
      }
    }
  }

  private buildRequest(): MailInitiateRequest {
    return {
      mailType:        this.mailType,
      mailSource:      this.mailType === 'INTERNAL_MEMO' ? 'MEMO' : 'UPLOAD',
      documentNodeId:  this.uploadedFile?.nodeId ?? '',
      subject:         this.subject,
      fromOrganization:this.fromOrganization,
      fromName:        this.fromName,
      fromEmail:       this.fromEmail,
      externalReference: this.externalReference,
      dateOfMail:      this.dateOfMail,
      dateReceived:    this.dateReceived,
      fileClassId:     Number(this.selectedFileClassId),
      fileReferenceId: this.selectedFileRefId !== '' ? Number(this.selectedFileRefId) : undefined,
      folio:           this.folio ?? undefined,
      priority:        this.priority,
      // Recipient — one of user or group
      dispatchTo:      this.selectedUser?.id      ?? undefined,
      dispatchToGroup: this.selectedGroup?.id     ?? undefined,
      // Optional
      deadlineAt:      this.deadlineAt             || undefined,
      escalateTo:      this.selectedEscalateTo?.id ?? undefined,
      instructions:    this.instructions           || undefined,
    };
  }
}